import React, { useId } from 'react';

import ChevronIcon from '@/assets/chevron.svg?react';
import OptionItem from '@/components/ui/ComboBox/OptionItem';
import { selectClasses } from '@/components/ui/Select/Select';
import { RESPONSIVE_QUERY } from '@/consts';
import { EnumLike } from '@/types';
import cn from '@/utils/cn';

import ErrorMessage from '../ErrorMessage';
import { FormControl, FormField, FormItem } from '../Form';
import { Input } from '../Input';
import { ScrollArea } from '../Select/ScrollArea';
import { Combobox as PrimitiveComboBox } from '@headlessui/react';
import { Float } from '@headlessui-float/react';
import { FieldValues } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';

interface Props {
  field: FieldValues;
  options?: EnumLike[];
  placeholder?: string;
  success?: boolean;
  error?: string;
  onChange?: (s: EnumLike) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onFocusOut?: () => void;
  query: string;
  setQuery: (value: string) => void;
  label?: string;
  className?: string;
  creatable?: boolean;
  isTextInput?: boolean;
  noResultsLabel?: string;
  // Text shown in the field for the selected option; the list keeps full labels.
  displayValue?: (label: string) => string;
}

const ControlledComboBox: React.FC<Props> = ({
  field,
  options = [],
  placeholder,
  success,
  error,
  className,
  onChange,
  onBlur,
  onFocus,
  onFocusOut,
  query,
  setQuery,
  label,
  creatable,
  isTextInput = false,
  noResultsLabel,
  displayValue,
}) => {
  const id = useId();

  const ref = React.useRef<HTMLInputElement>(null);
  const isDesktop = useMediaQuery({ query: RESPONSIVE_QUERY.IS_MD });
  const maxScrollAreaItems = isDesktop ? 7 : 5;

  const filteredItems = options;

  if (field.disabled) {
    return (
      <FormField
        name={field.name}
        disabled
        render={({ field }) => {
          return (
            <FormItem className="col-span-2">
              <FormControl>
                <Input
                  disabled
                  label={label}
                  data-testid={`input-disabled-${field.name}`}
                  {...field}
                  value=""
                  rightSection={
                    !isTextInput && (
                      <ChevronIcon
                        width={14}
                        height={14}
                        className="rotate-90 text-dark-gray-200"
                      />
                    )
                  }
                  classNameRightSection="right-4 top-1/2 -translate-y-1/2"
                />
              </FormControl>
            </FormItem>
          );
        }}
      />
    );
  }

  return (
    <div className={cn('relative flex w-full flex-col gap-1', className)}>
      <PrimitiveComboBox
        ref={ref}
        as="div"
        className={cn('relative block w-full', className)}
        onBlur={() => {
          if (onBlur) {
            filteredItems.length === 1 && field.onBlur(filteredItems[0]);
            onBlur();
            return;
          }
          filteredItems.length === 1 && field.onChange(filteredItems[0]);
        }}
        onChange={(value) => {
          if (!value) {
            field.onChange(null);
            return;
          }
          if (creatable) {
            if (value === '') return;
            options.push({
              label: value,
              value: value,
            });
          }
          if (onChange) {
            const selectedValue = options.find((item) => item?.label === value);
            if (selectedValue !== undefined) {
              onChange(selectedValue);
            }
            onFocusOut && onFocusOut();
            return;
          }
          field.onChange(options.find((item) => item?.label === value)?.value);
        }}
        onFocus={onFocus}
        value={
          options.find(
            (item) => String(item?.value) === String(field.value?.value)
          )?.label ||
          query ||
          ''
        }
      >
        {({ open }) => (
          <Float
            as="div"
            placement="bottom"
            enter="transition-all duration-100 ease-in-out"
            enterFrom="opacity-0 -translate-y-3"
            enterTo="opacity-100 translate-y-0"
            leave="transition-none duration-0 ease-in-out"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            offset={4}
            onHide={() => {
              setQuery('');
            }}
            portal
            adaptiveWidth
          >
            {/* TODO - merge styles with Combobox component */}
            <PrimitiveComboBox.Button className="group w-full" as="div">
              <PrimitiveComboBox.Input
                placeholder={placeholder}
                id={id}
                className={cn(
                  selectClasses(),
                  'cursor-pointer placeholder:text-dark-gray-200',
                  'body text-oxford-blue-500',
                  '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                  'relative pt-[26px]',
                  success &&
                    'border-green-400 hover:border-green-400 focus:border-green-400',
                  open
                    ? 'border-blue-600 hover:border-blue-600 focus:border-blue-600 focus:ring-0'
                    : 'focus:ring-4',
                  'group-hover:border-blue-600',
                  success &&
                    'border-green-400 group-hover:border-green-400 hover:border-green-400 focus:border-green-400',
                  !!error && 'border-red-400',
                  isTextInput && 'cursor-text'
                )}
                autoComplete="off"
                displayValue={
                  displayValue
                    ? (value: string) => (value ? displayValue(value) : '')
                    : undefined
                }
                onKeyDown={(event) => {
                  if (event.key === 'Backspace') {
                    field.onChange(null);
                    options = [];
                  }
                }}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
                onBlur={() => {
                  if (onFocusOut) onFocusOut();
                }}
                data-testid={`input-${field.name}`}
              />

              {label && (
                <span
                  className={cn(
                    'body pointer-events-none absolute left-3.5 translate-y-1/2 text-dark-gray-200 delay-75 duration-150',
                    field.value || query
                      ? 'input-label-small'
                      : 'group-focus-within:input-label-small leading-[26px]'
                  )}
                >
                  {label}
                </span>
              )}

              {!isTextInput && (
                <div className="absolute right-4 top-1/2 flex -translate-y-1/2 items-center gap-2.5">
                  <div className="flex items-center">
                    <ChevronIcon
                      width={14}
                      height={14}
                      className={cn(
                        'shrink-0 rotate-90 cursor-pointer text-dark-gray-300 transition-transform duration-300 ease-in-out',
                        open && 'rotate-[270deg]'
                      )}
                    />
                  </div>
                </div>
              )}
            </PrimitiveComboBox.Button>

            <PrimitiveComboBox.Options
              className={cn(
                'my-[1px] bg-white p-1.5',
                'w-[var(--radix-select-trigger-width)]',
                'data-[state=open]:duration-150 data-[state=closed]:ease-in-out data-[state=open]:ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[side=bottom]:slide-in-from-top-2',
                'rounded-lg border border-gray-200 shadow-shadow14',
                isTextInput && 'hidden'
              )}
            >
              <ScrollArea mah={'max-h-[156px] md:max-h-[266px]'}>
                <div
                  className={cn(
                    'flex flex-col gap-1 border-gray-400',
                    (query && filteredItems.length > maxScrollAreaItems) ||
                      (!query &&
                        options.length > maxScrollAreaItems &&
                        'pr-3.5')
                  )}
                >
                  {/* in order to not set first value */}
                  <OptionItem
                    item={{ value: '', label: '' }}
                    key={'empty'}
                    className={cn('hidden')}
                    data-testid={`option-${field.name}-empty-option`}
                  />
                  {!query &&
                    options.map((item) => (
                      <OptionItem
                        item={item}
                        key={item?.label}
                        data-testid={`option-${field.name}-${item?.value}`}
                        className={cn(
                          String(item?.value) === String(field.value?.value) &&
                            'font-semibold'
                        )}
                      />
                    ))}
                  {query &&
                    filteredItems.map((item) => (
                      <OptionItem
                        item={item}
                        key={item?.label}
                        data-testid={`option-${field.name}-${item?.value}`}
                        className={cn(
                          String(item?.value) === String(field.value?.value) &&
                            'font-semibold'
                        )}
                      />
                    ))}
                  {query &&
                    query.length > 2 &&
                    filteredItems.length === 0 &&
                    (creatable ? (
                      <OptionItem
                        item={{ label: `${query}`, value: query }}
                        data-testid={`option-${field.name}-${query}`}
                        key={'No results found'}
                      />
                    ) : (
                      <OptionItem
                        item={{
                          label: noResultsLabel || 'Brak wyników',
                          value: 'none',
                        }}
                        key={'No results found'}
                        data-testid={`option-${field.name}-no-results`}
                        className={cn(
                          'pointer-events-none cursor-not-allowed hover:bg-white'
                        )}
                      />
                    ))}
                  {(!query || query.length <= 2) &&
                    filteredItems.length === 0 && (
                      <OptionItem
                        item={{
                          label: 'Wprowadź co najmniej 3 znaki',
                          value: 'none',
                        }}
                        key={'No query found'}
                        data-testid={`option-${field.name}-no-query`}
                        className={cn(
                          'pointer-events-none cursor-not-allowed hover:bg-white'
                        )}
                      />
                    )}
                </div>
              </ScrollArea>
            </PrimitiveComboBox.Options>
          </Float>
        )}
      </PrimitiveComboBox>
      {!!error && <ErrorMessage errorMessage={error} className="mt-0" />}
    </div>
  );
};

export default ControlledComboBox;
